import type {ReactElement} from 'react';
import React, {useEffect} from 'react';
import {ThumbnailItemSelectionState} from '@Components/thumbnail-item';
import {Icon} from '@Components/icon-v2';
import {IconSize} from '@Components/icon-v2/icon.types';
import {Thumbnail} from '@Components/thumbnail';
import {ThumbnailType} from '@Components/thumbnail/thumbnail.types';
import type {FillObject} from '@PosterWhiteboard/classes/fill.class';
import {FillTypes} from '@PosterWhiteboard/classes/fill.class';
import {updateOptionsPanelHeight} from '@Components/poster-editor/library/poster-editor-library';
import {ContextualMenuThumbnailItem} from '@Components/contextual-menu/components/contextual-menu-thumbnail-item';
import {getAssetUrl} from '@Utils/s3.util';
import {getCompatibleImageFileExtension} from '@Utils/image.util';
import styles from './background-type-option.module.scss';

interface BackgroundTypeOptionProps {
  panelId: string;
  fillObject: FillObject;
  updateBackgroundFillType(fillType: FillTypes): void;
  heightOffset?: number;
}

const THUMBNAIL_BASE_URL = 'poster-editor-thumbnails/text-background/';

export function BackgroundTypeOption({heightOffset = 0, ...props}: BackgroundTypeOptionProps): ReactElement | null {
  useEffect((): void => {
    updateHeightInRedux(props.fillObject.fillType);
  }, [props.fillObject.fillType]);

  const updateHeightInRedux = (newType: FillTypes): void => {
    let height: number;
    switch (newType) {
      case FillTypes.LINEAR_GRADIENT:
      case FillTypes.RADIAL_GRADIENT:
        height = 360;
        break;
      case FillTypes.SOLID:
        height = 310;
        break;
      case FillTypes.NONE:
      default:
        height = 178;
    }

    if (newType !== FillTypes.NONE) {
      height += heightOffset;
    }

    updateOptionsPanelHeight(props.panelId, `${height}px`);
  };

  const onUpdateBackgroundType = (newType: FillTypes): void => {
    if (newType === props.fillObject.fillType) {
      return;
    }
    updateHeightInRedux(newType);
    props.updateBackgroundFillType(newType);
  };

  return (
    <ul className={`flex-h-row spacing-m-0 ${styles.minHeight}`}>
      <ContextualMenuThumbnailItem
        isMultiLineText
        thumbnail={<Icon icon="icon-ban" size={IconSize.SIZE_ICON_32} />}
        isSelected={props.fillObject.fillType === FillTypes.NONE}
        selectionState={ThumbnailItemSelectionState.OUTLINE}
        text={window.i18next.t('pmwjs_none')}
        textClassName="content-body spacing-m-t-2"
        onClick={(): void => {
          onUpdateBackgroundType(FillTypes.NONE);
        }}
      />
      <ContextualMenuThumbnailItem
        isMultiLineText
        thumbnail={
          <Thumbnail
            className="radius-8"
            data={{
              type: ThumbnailType.IMAGE,
              imageSrc: getAssetUrl(`${THUMBNAIL_BASE_URL}solid.${getCompatibleImageFileExtension('webp')}`),
            }}
          />
        }
        isSelected={props.fillObject.fillType === FillTypes.SOLID}
        selectionState={ThumbnailItemSelectionState.OUTLINE}
        text={window.i18next.t('pmwjs_background_solid')}
        textClassName="content-body"
        onClick={(): void => {
          onUpdateBackgroundType(FillTypes.SOLID);
        }}
      />
      <ContextualMenuThumbnailItem
        isMultiLineText
        thumbnail={
          <Thumbnail
            className="radius-8"
            data={{
              type: ThumbnailType.IMAGE,
              imageSrc: getAssetUrl(`${THUMBNAIL_BASE_URL}linear.${getCompatibleImageFileExtension('webp')}`),
            }}
          />
        }
        isSelected={props.fillObject.fillType === FillTypes.LINEAR_GRADIENT}
        selectionState={ThumbnailItemSelectionState.OUTLINE}
        text={window.i18next.t('pmwjs_linear_gradient_background')}
        textClassName={`content-body spacing-p-l-3 spacing-p-r-3 ${styles.wrapText}`}
        onClick={(): void => {
          onUpdateBackgroundType(FillTypes.LINEAR_GRADIENT);
        }}
      />
      <ContextualMenuThumbnailItem
        isMultiLineText
        thumbnail={
          <Thumbnail
            className="radius-8"
            data={{
              type: ThumbnailType.IMAGE,
              imageSrc: getAssetUrl(`${THUMBNAIL_BASE_URL}radial.${getCompatibleImageFileExtension('webp')}`),
            }}
          />
        }
        isSelected={props.fillObject.fillType === FillTypes.RADIAL_GRADIENT}
        selectionState={ThumbnailItemSelectionState.OUTLINE}
        text={window.i18next.t('pmwjs_radial_gradient_background')}
        textClassName={`content-body spacing-p-l-3 spacing-p-r-3 ${styles.wrapText}`}
        onClick={(): void => {
          onUpdateBackgroundType(FillTypes.RADIAL_GRADIENT);
        }}
      />
    </ul>
  );
}
